import { Component, OnInit, AfterViewInit, Inject, ViewChild } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatStep, MatStepper } from '@angular/material/stepper'
import { Store, select } from '@ngrx/store'
import { Channel, Destroyable, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { AVAIL_PUSH_STAMP_FUTURE_SKEW, Connection, stampedSinceLink } from '@tv3/store/connection/connection.model'
import { selectConnectionById } from '@tv3/store/connection/connection.selectors'
import { BDCListingWizardStep1Component } from './step1/bdc-listing-wizard-step1.component'
import { BDCListingWizardStep2Component } from './step2/bdc-listing-wizard-step2.component'

@Component({
  selector: 'app-bdc-listing-wizard',
  templateUrl: './bdc-listing-wizard.component.html',
  styleUrls: ['./bdc-listing-wizard.component.scss'],
  host: {
    class: 'modal-content',
  },
})
export class BDCListingWizardComponent extends Destroyable implements OnInit, AfterViewInit {
  @ViewChild(MatStepper) stepper: MatStepper
  @ViewChild('rates') ratesStep: MatStep
  @ViewChild(BDCListingWizardStep1Component) step1Cmp: BDCListingWizardStep1Component
  @ViewChild(BDCListingWizardStep2Component) step2Cmp: BDCListingWizardStep2Component

  get channel() {
    return this.data.channel
  }

  get connection() {
    return this.data.connection
  }

  // Step completion derived from the durable connection timestamps (ignoring stamps that
  // predate the current rental link), so a reopened wizard resumes where the connection
  // actually is instead of restarting from the first step. BDC records these timestamps
  // server-side only when the scheduled work really completes, so right after a push the
  // store may not carry them yet — the step components' own session flags fill that gap;
  // without them the linear stepper would refuse to advance past a step it shows as done.
  get isConnectingStepDone(): boolean {
    const connection = this.connection
    return (
      !!(
        connection?.rentalId &&
        stampedSinceLink(connection, connection?.lastavailpull) &&
        stampedSinceLink(connection, connection?.lastavailpush, AVAIL_PUSH_STAMP_FUTURE_SKEW)
      ) || !!this.step1Cmp?.isStepComplete
    )
  }

  get isRatesStepDone(): boolean {
    return stampedSinceLink(this.connection, this.connection?.lastratepush) || !!this.step2Cmp?.isRatesPushed
  }

  constructor(
    public dialogRef: MatDialogRef<BDCListingWizardComponent>,
    private store: Store<any>,
    @Inject(MAT_DIALOG_DATA) public data: { channel: Channel; connection: Connection }
  ) {
    super()
  }

  ngOnInit(): void {
    this.store.pipe(select(selectConnectionById, { id: this.connection.id }), untilDestroy(this)).subscribe((conn) => {
      this.data.connection = conn
    })
  }

  ngAfterViewInit(): void {
    // Resume to the first incomplete step. Deferred a tick so the stepper has rendered
    // and the [completed] bindings are in effect before the linear guard runs.
    setTimeout(() => {
      if (!this.stepper) {
        return
      }
      if (this.isConnectingStepDone && this.isRatesStepDone) {
        this.stepper.selectedIndex = 2
      } else if (this.isConnectingStepDone) {
        this.ratesStep.select()
      }
    })
  }

  close() {
    this.dialogRef.close()
  }

  onChannel() {
    this.dialogRef.close('channel')
  }

  onRatesStep() {
    // Deferred a tick: the auto-chain emits `next` synchronously with the store update
    // that completes the Connecting step, and the linear guard would still read the
    // step's previous [completed]="false" binding until change detection runs.
    setTimeout(() => this.ratesStep.select())
  }
}
